import { useQuery } from "@apollo/client/react";
import { useDebouncedValue } from "@repo/components/src/shared/useDebouncedValue";
import { CreatorPaymentBreakdownDocument } from "@repo/graphql/documents/tax/query.generated";
import { useMemo } from "react";

type CreatorPaymentBreakdownItem = {
  amount: {
    amount: string;
    currency: string;
  };
  userId?: string;
};

type UseCreatorPaymentBreakdownArgs = {
  brandId: string | undefined;
  items: CreatorPaymentBreakdownItem[];
};

export const useCreatorPaymentBreakdown = ({
  brandId,
  items,
}: UseCreatorPaymentBreakdownArgs) => {
  // `items` is typically built fresh on every render (e.g. `.map()` over form
  // state), so stringify it into a stable key the debounce hook can compare by
  // value instead of by reference — otherwise every render would count as a change
  // and the debounce would never settle.
  const itemsKey = JSON.stringify(items);
  const debouncedItemsKey = useDebouncedValue(itemsKey);
  const debouncedItems = useMemo<CreatorPaymentBreakdownItem[]>(
    () => JSON.parse(debouncedItemsKey),
    [debouncedItemsKey],
  );
  const hasAmount = debouncedItems.some(
    (item) => Number(item.amount.amount) > 0,
  );

  const { data, previousData, loading } = useQuery(
    CreatorPaymentBreakdownDocument,
    {
      variables: {
        brandId: brandId ?? "",
        items: debouncedItems,
      },
      skip: !brandId || !hasAmount,
    },
  );

  const breakdown = useMemo(() => {
    const result = data ?? previousData;
    return result?.tax.creatorPaymentBreakdown ?? null;
  }, [data, previousData]);

  return { breakdown, loading };
};
